import { useState, useRef } from 'react'
import cx from 'classnames'
import Styles from './PromptBar.module.scss'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { EventWizardButton, NonChannelWizard } from 'components/DocRightRail/screens/AIAssistant/EventWizardButton'
import { ChangeLengthAction } from './ChangeLengthAction'
import { ChangeToneAction } from './ChangeToneAction'
import { ImproveQualityAction } from './ImproveQualityAction'
import { PromptInput } from 'components/PromptInput/PromptInput'
import { EditorView } from 'prosemirror-view'
import { AllSelection, NodeSelection, TextSelection } from 'prosemirror-state'
import { useCreateRevision, Selection } from 'hooks/queries/layers/revisions/useCreateRevision'
import { markdownSerializer } from 'common/Prose/components/TextInlineFormatBar/components/FormatBars/markdownSerializer'
import { AiClient } from 'api/clients/AiClient'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { injectContent } from 'common/Prose/utils/injectContent'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { RevisionType } from 'components/BlazeCommandLine/Types'
import { useInteractOutside } from '@react-aria/interactions'
import { useCreateAISuggestionNode } from 'hooks/queries/layers/aiSuggestionNodes/useCreateAISuggestionNode'
import { useMutation } from 'react-query'
import { useLastEvent } from 'hooks/useLastEvent'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { TimelineEventFilter } from 'api/clients/ArticleClient'
import { ArticleAIGeneratedEvent } from 'components/ActivityEvent/Types'
import { TimelineEventType } from 'screens/Activity/Types'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

interface Props {
  articleId: string
  layerId: string
  view: EditorView
  anchor?: HTMLElement
  hidePromptBar?: boolean
}

enum IntentType {
  Revise = 'revise',
  Generate = 'generate',
}

const deserializeIntent = (intent: string): IntentType | undefined => {
  switch (intent.toLowerCase()) {
    case 'revise':
      return IntentType.Revise
    case 'generate':
      return IntentType.Generate
    default:
      return undefined
  }
}

const analyzeIntent = async (variables: { prompt: string; documentAsMarkdown?: string }) => {
  const response = await AiClient.analyzeIntent(variables)

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error()
  }

  return response.data
}

export const GenericPromptBar = ({ articleId, layerId, view }: Props) => {
  const isSystemRedesign = useIsSystemRedesign()
  const [customPrompt, setCustomPrompt] = useState('')
  const [containerFocused, setContainerFocused] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const { isMobileLg } = useResponsiveDesign()
  const mainContentRef = useRef(document.getElementById('main-content'))

  const entry = useIntersectionObserver(mainContentRef, { threshold: [0] })
  const isHidden = entry?.isIntersecting ?? false

  const { mutate: analyze, isLoading: isAnalyzing } = useMutation(analyzeIntent, {
    onError: () => {
      showToastMessage('An error occurred when processing your prompt', 'error')
    },
    onSuccess: (data) => {
      const intent = deserializeIntent(data.intent)

      if (!intent) {
        showToastMessage('An error occurred when processing your prompt', 'error')
        return
      }

      if (intent === IntentType.Revise) {
        handleRevise()
      } else {
        handleGenerate()
      }

      setCustomPrompt('')
    },
  })

  const { mutate: createAISuggestionNode, isLoading: isCreatingNode } = useCreateAISuggestionNode({
    articleId,
    layerId,
  })

  const { mutate: revisionMutation, isLoading: isRevising } = useCreateRevision(
    { articleKey: articleId, layerId },
    {
      onSuccess: () => {
        const tr = view.state.tr
        tr.setSelection(TextSelection.create(view.state.doc, 0))
        view.dispatch(tr)
      },
    },
  )

  const handleRevise = () => {
    const selection = new AllSelection(view.state.doc).toJSON() as Selection
    const doc = view.state.doc
    const documentAsMarkdown = markdownSerializer.serialize(doc)

    revisionMutation(
      {
        prompt: { custom_prompt: customPrompt, revision_type: RevisionType.CUSTOM_PROMPT },
        selection,
        selectionAsMarkdown: documentAsMarkdown,
        documentAsMarkdown,
      },
      {
        onError: () => {
          showToastMessage('An error occurred when revising your content', 'error')
        },
      },
    )
  }

  const handleGenerate = () => {
    let lastNode = view.state.doc.content.lastChild

    if (!lastNode || lastNode.type !== view.state.schema.nodes.paragraph || !!lastNode.content.size) {
      const tr = view.state.tr
      const node = view.state.schema.nodes.paragraph.create(null)
      tr.insert(tr.doc.content.size - 1, node)
      view.dispatch(tr)
      lastNode = tr.doc.content.lastChild
    }

    if (!lastNode) return

    const pos = view.state.doc.content.size - lastNode.nodeSize
    const selection = new NodeSelection(view.state.doc.resolve(pos))
    const newDoc = injectContent(view.state, selection, '<<AI_CONTENT>>')
    const documentAsMarkdown = markdownSerializer.serialize(newDoc)

    createAISuggestionNode(
      {
        selection,
        documentAsMarkdown,
        prompt: {
          generation_type: GenerationType.InlineGeneric,
          topic: customPrompt,
        },
      },
      {
        onError: () => {
          showToastMessage('An error occurred when generating your content', 'error')
        },
      },
    )
  }

  const handleSubmit = async () => {
    const doc = view.state.doc
    const documentAsMarkdown = markdownSerializer.serialize(doc)

    analyze({ prompt: customPrompt, documentAsMarkdown })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation()
    if (e.key === 'Enter' && !e.shiftKey && customPrompt.trim().length > 0) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handlePromptFocused = (isFocused: boolean) => {
    if (isFocused) setContainerFocused(isFocused)
  }

  const handleClickOutside = () => {
    setContainerFocused(false)
  }

  useInteractOutside({ ref: wrapperRef, onInteractOutside: handleClickOutside })

  const { lastEvent } = useLastEvent({ articleId, layerId, initialFilters: [TimelineEventFilter.AI] })

  const lastAIEvent =
    lastEvent?.type === TimelineEventType.ArticleAIGeneratedEvent ? (lastEvent as ArticleAIGeneratedEvent) : undefined

  return (
    <div
      className={cx(Styles.wrapper, { [Styles.focused]: containerFocused, [Styles.hidden]: !isHidden })}
      ref={wrapperRef}
    >
      {containerFocused && (
        <div className={Styles.optionsContainer}>
          <div className={Styles.topRow}>
            <div className={Styles.topLeft}>
              <div className={Styles.leftActions}>
                <ChangeLengthAction articleId={articleId} layerId={layerId} view={view} />
                <ChangeToneAction articleId={articleId} layerId={layerId} view={view} />
                <ImproveQualityAction view={view} articleId={articleId} layerId={layerId} />
              </div>
            </div>
            <div className={Styles.topRight}>
              {lastAIEvent &&
                [...Object.values(ChannelType), ...Object.values(NonChannelWizard)].includes(
                  lastAIEvent.data.generation_type as ChannelType,
                ) && (
                  <EventWizardButton
                    event={lastAIEvent}
                    title={isMobileLg ? 'Revise' : 'Revise Prompt'}
                    size={isMobileLg ? 'sm' : 'md'}
                  />
                )}
            </div>
          </div>
        </div>
      )}
      <PromptInput
        className={Styles.promptInput}
        prompt={customPrompt}
        onChange={setCustomPrompt}
        onSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
        isDisabled={isRevising || isCreatingNode || isAnalyzing}
        onFocusChange={handlePromptFocused}
        placeholder="Ask Blaze to edit or generate..."
        largeInput
        withStartIcon={!isSystemRedesign}
      />
    </div>
  )
}
